import { Grid, Typography } from '@material-ui/core'
import { RARITIES } from 'core/constants'
import { AssetType } from 'core/logic/asset/asset.types'
import { useMemo } from 'react'
import { t, tDefault, tDefaultWithLog } from 'translate/i18n'
import { AssetFilterTestId } from './AssetFilters.e2e'
import {
  Filter,
  MarketplaceAssetFilter,
  MarketplaceFilter,
  MarketplaceOrderFilter,
} from './AssetFilters.types'
import useStyles from './AssetFilters.styles'
import { ToggleButton } from '@material-ui/lab'
import CurrencyLabel from 'components/CurrencyLabel/CurrencyLabel.view'
import Tabs from '@onepercentio/one-ui/components/Tabs'
import Button from '@onepercentio/one-ui/components/Button'
import Select from '@onepercentio/one-ui/components/Select'
import { AssetListTestId } from 'pages/Unauthenticated/Asset/AssetList.e2e'
import Input from '@onepercentio/one-ui/components/Input'
import IconRarum from 'components/IconRarum'
import { OPEN_TEXT_FIELD } from './AssetFilters.data'
import SCSSStyles from './AssetFilters.module.scss'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import {
  useFilterOptionsFromAssetsList,
  useFilterOptionsFromFilterGraph,
  V1Options,
} from './AssetFilters.hook'

export default function AssetFilters({
  origin,
  allAvailableAssets,
  onRarityFilter,
  onTraitFilter,
  onNoFilter,
  onTextFilter,
  counters,
  filterOptions,
  ...otherProps
}: {
  origin: 'myCollection' | 'marketplace'
  allAvailableAssets: AssetType[]
  onRarityFilter: (filter: string) => void
  onTraitFilter: (key: string, _value: string | unknown) => void
  onNoFilter: () => void
  onTextFilter: (query: string) => void
  counters: {
    [k in MarketplaceAssetFilter]: number
  }
  filterOptions: V1Options
} & (
  | {
      marketplace: true
      filter: MarketplaceFilter
      onCurrencyFilter: (currency: string) => void
      onAssetFilter: (filter: MarketplaceAssetFilter) => void
      onOrderFilter: (order: MarketplaceOrderFilter) => void
      currencies: string[]
    }
  | {
      marketplace?: false
      filter: Filter
    }
)) {
  const classes = useStyles()
  const marketplace = otherProps.marketplace
  const filter = otherProps.filter
  const { rarity: rarityFilter, trait: traitFilter } = filter
  const rarityFilters = useMemo(() => {
    return RARITIES.filter((rarity: string) =>
      allAvailableAssets.find((asset: AssetType) => asset.class === rarity)
    )
  }, [allAvailableAssets])

  const RarityFilterButtons = useMemo(() => {
    if (rarityFilters.length === 0) return null
    if (!marketplace)
      return rarityFilters
        .map((_filter) => (
          <Button
            className={filter.rarity === _filter ? 'active' : ''}
            variant='transparent'
            onClick={() => onRarityFilter(_filter)}
            key={_filter}>
            {t(`unauthenticated.galleryCards.filter.${_filter}`)}
          </Button>
        ))
        .concat(<>&nbsp;&nbsp;&nbsp;</>)
    else
      return (
        <Select
          label={t('marketplace.filters.rarity.label')}
          onClick={({ value: rarity }) => onRarityFilter(rarity)}
          data-testid={`${AssetFilterTestId.DROPDOWN_SELECT_PREFIX}${t(
            'marketplace.filters.rarity.label'
          )}`}
          items={rarityFilters.map((r) => ({
            label: t(`marketplace.filters.rarity.types.${r}`) as string,
            value: r,
          }))}
          selected={rarityFilter as (typeof rarityFilters)[number]}
        />
      )
  }, [marketplace, rarityFilter, rarityFilters, onRarityFilter])

  const { traitFilters } = filterOptions

  const TraitFilterButtons = Object.keys(traitFilters).map((key) => {
    const label = tDefaultWithLog(
      `unauthenticated.galleryCards.filter.traits.${key as 'Type'}`,
      key
    )
    const traitFilterValue = traitFilter ? traitFilter[key] : undefined
    const selectSize = (traitFilterValue || label).length
    return (
      <Select
        key={key}
        selected={traitFilterValue}
        label={label}
        size={selectSize}
        data-testid={`${AssetListTestId.DROPDOWN_SELECT_PREFIX(key)}`}
        onClick={({ value }) => onTraitFilter(key, value)}
        items={traitFilters[key]}
      />
    )
  })

  return (
    <Grid
      container
      data-testid={AssetFilterTestId.FILTERS_ROW}
      className={`${classes.filters} ${SCSSStyles.root}`}>
      {OPEN_TEXT_FIELD && (
        <>
          <Input
            onChange={({ target: { value } }) => onTextFilter(value)}
            decoration={<IconRarum icon='search' size={24} />}
            placeholder={t('unauthenticated.galleryCards.filter.open')}
            data-testid={AssetFilterTestId.OPEN_TEXT_FIELD}
            className={SCSSStyles.search}
          />
          <Spacing size='8' />
        </>
      )}
      {marketplace && (
        <div className={classes.row}>
          <AssetModeSelector
            mode={origin}
            onSelected={otherProps.onAssetFilter}
            selected={otherProps.filter.asset}
            counters={counters}
          />
          {origin === 'marketplace' && (
            <OrderSelector
              onSelected={otherProps.onOrderFilter}
              selected={otherProps.filter.order}
            />
          )}
        </div>
      )}
      <div className={SCSSStyles.traitsRow}>
        {!marketplace && (RarityFilterButtons || TraitFilterButtons) ? (
          <Button
            className={filter.rarity === null ? 'active' : ''}
            variant='transparent'
            onClick={() => onNoFilter()}>
            {t('unauthenticated.galleryCards.filter.all')}
          </Button>
        ) : null}
        {RarityFilterButtons}
        {TraitFilterButtons}

        {marketplace && origin === 'marketplace' && (
          <CurrencySelector
            currencies={otherProps.currencies}
            selected={otherProps.filter.currency || ''}
            onSelected={otherProps.onCurrencyFilter}
          />
        )}
      </div>
      {marketplace && (
        <Typography
          data-testid={AssetFilterTestId.CLEAR_FILTERS_BTN}
          component={'a'}
          onClick={onNoFilter}>
          {t('marketplace.filters.clear')}
        </Typography>
      )}
    </Grid>
  )
}
function OrderSelector({
  selected,
  onSelected,
}: {
  selected: MarketplaceOrderFilter
  onSelected: (s: MarketplaceOrderFilter) => void
}) {
  return (
    <Select
      onClick={({ value }) => onSelected(value)}
      items={[
        {
          value: MarketplaceOrderFilter.PRICE_ASC,
          label: t('marketplace.filters.order.lowestToHighest') as string,
        },
        {
          value: MarketplaceOrderFilter.PRICE_DES,
          label: t('marketplace.filters.order.highestToLowest') as string,
        },
      ]}
      selected={selected}
    />
  )
}

function AssetModeSelector({
  mode,
  selected,
  onSelected,
  counters,
}: {
  mode: 'myCollection' | 'marketplace'
  selected: MarketplaceAssetFilter
  onSelected: (filter: MarketplaceAssetFilter) => void
  counters: {
    [k in MarketplaceAssetFilter]: number
  }
}) {
  const classes = useStyles()
  const prefix =
    mode === 'myCollection'
      ? 'authenticated.myCollection.filter'
      : 'marketplace.filters.assets'
  return (
    <Tabs
      onSelect={onSelected}
      selected={selected}
      itemClassName={classes.tab}
      options={[
        {
          id: MarketplaceAssetFilter.ALL,
          label: t(`${prefix}.showAll`, {
            amount: counters[MarketplaceAssetFilter.ALL],
          }),
        },
        {
          id: MarketplaceAssetFilter.ONLY_ON_SALE,
          label: t(`${prefix}.cardsOnSale`, {
            amount: counters[MarketplaceAssetFilter.ONLY_ON_SALE],
          }),
        },
      ]}
    />
  )
}

function CurrencySelector({
  currencies,
  onSelected,
  selected,
}: {
  currencies: string[]
  onSelected: (currency: string) => void
  selected: string
}) {
  const classes = useStyles()
  return (
    <>
      <div className={classes.pushToRight} />
      <div>
        <Typography>{t('marketplace.filters.currency')}</Typography>
        <div>
          {currencies.map((currency) => (
            <ToggleButton
              key={currency}
              classes={{
                root: classes.tag,
              }}
              selected={selected === currency}
              onClick={() => onSelected(currency)}>
              <CurrencyLabel currency={currency} />
            </ToggleButton>
          ))}
        </div>
      </div>
    </>
  )
}
